// AIDEV-NOTE: Edgetag-specific track helpers. These wrap analytics.track() with the canonical Edgetag
// payload shape and use plugin scoping ({plugins: {all:false, edgetag:true}}) so we don't pollute
// GA4 / GTM / Mixpanel with Edgetag-shaped events. PetPlate already fires GA4-shape ecommerce events
// elsewhere — these helpers are strictly for the Edgetag pipeline.
// Reference: https://docs.edgetag.io/overview/standard-events
import type { AnalyticsInstance } from 'analytics';

// AIDEV-NOTE: Edgetag Content object — see https://docs.edgetag.io/overview/standard-events#content-object
// id, quantity, item_price are required when contents[] is provided.
export type EdgetagContent = {
  id: string;
  quantity: number;
  item_price: number;
  variantId?: string;
  sku?: string;
  title?: string;
  description?: string;
  category?: string;
  brand?: string;
  type?: 'product' | 'product_group';
  image?: string;
  url?: string;
};

const EDGETAG_ONLY = { plugins: { all: false, edgetag: true } } as const;

// AIDEV-NOTE: Factory returns helpers bound to a specific analytics instance. apps/web and apps/subflow
// each instantiate their own analytics() — they call createEdgetagHelpers(analytics) and re-export.
export const createEdgetagHelpers = (analytics: AnalyticsInstance) => ({
  trackViewContent: (payload: {
    currency?: string;
    value?: number;
    contents?: EdgetagContent[];
  }) => {
    analytics.track('view_content', payload, EDGETAG_ONLY);
  },

  trackAddToCart: (payload: {
    currency: string;
    value: number;
    contents?: EdgetagContent[];
    checkoutUrl?: string;
  }) => {
    analytics.track('add_to_cart', payload, EDGETAG_ONLY);
  },

  trackInitiateCheckout: (payload: {
    currency: string;
    value: number;
    contents?: EdgetagContent[];
    checkoutUrl?: string;
  }) => {
    analytics.track('initiate_checkout', payload, EDGETAG_ONLY);
  },

  trackLead: (payload: {
    currency?: string;
    value?: number;
    name?: string;
    category?: string;
    email?: string;
  }) => {
    // AIDEV-NOTE: PET-112 — email is included on the Lead tag so Blotout forwards it to Klaviyo,
    // which associates it with the lead record. Standard Edgetag Lead has no email field; this is
    // an additive PetPlate/Blotout convention (other fields unchanged).
    analytics.track('lead', payload, EDGETAG_ONLY);
  }
});

// AIDEV-NOTE: setEdgetagUser fires the raw `edgetag('data', {...})` call used for identity binding.
// PET-112: email is sent under the `email` key (Blotout/Klaviyo reads `email`, not the Meta-shorthand
// `em`). Remaining identity fields keep Meta shorthand (ph, fn, ln, ...). Edgetag hashes values
// automatically. Required by Blotout for the Lead flow: fire Lead (now incl. email) AND
// setEdgetagUser({email}) together.
export type EdgetagUserData = {
  email?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
  external_id?: string;
};

export const setEdgetagUser = (data: EdgetagUserData) => {
  if (typeof window === 'undefined' || typeof window.edgetag !== 'function') return;
  window.edgetag('data', data);
};

// AIDEV-NOTE: PET-113 — Blotout's loaded SDK returns the user id SYNCHRONOUSLY from
// edgetag('getUserId') and does NOT invoke a callback. The previous callback-only implementation
// therefore always hit the timeout → resolved null → et_u_id was never appended on the
// subflow → checkout redirect → ~100% of purchases mis-attributed to direct/referral in GA4.
// We now read the synchronous return value first (current SDK), and still support a promise
// return or the callback form (older/other contracts). The timeout is the last-resort fallback
// for when only the queue stub is present (SDK not yet loaded, or blocked by an ad blocker).
// Returns null on timeout, missing SDK, empty id, or SDK error — never throws.
// Reference: https://docs.edgetag.io/implementation/browser/user-id
export const getEdgetagUserId = (timeoutMs = 1000): Promise<string | null> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.edgetag !== 'function') {
      resolve(null);
      return;
    }
    let settled = false;
    const finish = (value: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(typeof value === 'string' && value.length > 0 ? value : null);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      // edgetag is typed to return void (the queue stub); the loaded SDK returns the id. Cast to read it.
      const edgetag = window.edgetag as (...args: unknown[]) => unknown;
      const returned = edgetag('getUserId', (id?: string) => finish(id));
      if (returned && typeof (returned as { then?: unknown }).then === 'function') {
        (returned as Promise<string>).then(finish, () => finish(null));
      } else if (typeof returned === 'string' && returned.length > 0) {
        finish(returned);
      }
    } catch {
      finish(null);
    }
  });

// AIDEV-NOTE: Append the et_u_id query param to a destination URL for cross-subdomain identity hop.
// Returns the input URL unchanged when userId is empty or URL parsing fails.
export const appendEdgetagUserId = (url: string, userId: string | null): string => {
  if (!userId) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('et_u_id', userId);
    return parsed.toString();
  } catch {
    return url;
  }
};
